import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import GetCookie from "../../utils/GetCookie";
import ReadMoreTruncateText from "../../utils/ReadMoreTruncateText";

const useOutsideClick = (ref, callback) => {
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [ref, callback]);
};

const TextractorModelLibraryList = ({ isOpen, onClose, onImport, onError }) => {
  const [models, setModels] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [importingId, setImportingId] = useState(null);
  const [expandedTagsModelId, setExpandedTagsModelId] = useState(null);

  const API_BASE_URL = "/pceapi/textractor";
  const headers = { Authorization: GetCookie("access_token") };

  const listRef = useRef(null);
  useOutsideClick(listRef, onClose);

  useEffect(() => {
    if (!isOpen || models.length > 0) return;
    if (isOpen) {
      const fetchModels = async () => {
        setIsLoading(true);
        try {
          const response = await axios.get(`${API_BASE_URL}/list-model-libraries`, { headers });
          if (response.data.status === "success") {
            setModels(response.data.data);
          } else {
            onError(response.data.message || "Failed to fetch model libraries.");
          }
        } catch (error) {
          onError("An error occurred while fetching model libraries.");
          console.error("Error fetching model libraries:", error);
        } finally {
          setIsLoading(false);
        }
      };
      fetchModels();
    }
  }, [isOpen]);

  const handleImportClick = async (modelId) => {
    setImportingId(modelId);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/import-model-library`,
        { library_model_id: modelId },
        { headers }
      );
      if (response.data.status === "success") {
        onImport(response.data.data);
        onClose();
      } else {
        onError(response.data.message || "Failed to import model.");
      }
    } catch (error) {
      onError("An error occurred while importing the model.");
      console.error("Error importing model:", error);
    } finally {
      setImportingId(null);
    }
  };

  const toggleTagsExpansion = (modelId) => {
    setExpandedTagsModelId((prevId) => (prevId === modelId ? null : modelId));
  };

  if (!isOpen) return null;

  return (
    <div
      ref={listRef}
      className="ev-message-card2 d-block ev-card-3 position-absolute z-1000 mt-1 w-100 p-0"
      
    >
      <div className="scrollable-element vh-30 p-3">
        {isLoading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : models.length > 0 ? (
          <div>
            {models.map((model) => {
              const showAllTags = expandedTagsModelId === model.id;
              const visibleTags = showAllTags
                ? model.tags
                : model.tags?.slice(0, 2) || [];

              return (
                <div
                  key={model.id}
                  className="ev-card-7 d-flex justify-content-between align-items-center mb-2"
                >
                  <div className="flex-grow-1 pe-3">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <p className="mb-1 ev-reg-p-blue">{model.library_name}</p>
                      <button
                        className="ev-btn-blue-fill-round ev-bold-reg px-4"
                        onClick={() => handleImportClick(model.id)}
                        disabled={importingId === model.id}
                      >
                        {importingId === model.id ? (
                          <div className="px-2">
                            <span
                              className="spinner-border spinner-border-sm me-1"
                              role="status"
                              aria-hidden="true"
                            ></span>
                          </div>
                        ) : (
                          "Import"
                        )}
                      </button>
                    </div>
                    <ReadMoreTruncateText text={model.description} limit={50} />

                    {model.tags && model.tags.length > 0 && (
                      <div className="d-flex flex-wrap align-items-center gap-1 mt-1">
                        {visibleTags.map((tag, idx) => (
                          <span key={idx} className="ev-message-card6 border-0 mt-1">
                            {tag}
                          </span>
                        ))}
                        {model.tags.length > 2 && !showAllTags && (
                          <span
                            className="more-circle more-circle--small ev-btn-blue-outline-round ev-bold-reg"
                            onClick={() => toggleTagsExpansion(model.id)}
                            data-tooltip="Show more tags"
                            role="button"
                          >
                            +{model.tags.length - 2}
                          </span>
                        )}
                        {showAllTags && (
                          <span
                            className="more-circle more-circle--small ev-btn-blue-outline-round ev-bold-reg"
                            onClick={() => toggleTagsExpansion(model.id)}
                            data-tooltip="Hide tags"
                            role="button"
                          >
                            <i className="bi bi-dash"></i>
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="ev-alert-card-warning text-center py-3">
            <p className="mb-0">
              <i className="bi bi-info-circle me-1"></i> No models are available in the library.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TextractorModelLibraryList;